import { useRouter } from 'next/router';
import { useMemo } from 'react';
import {
  Modal,
  ModalBody,
  ModalFooterDefault,
  ModalHeaderDefault,
  Paragraph,
  useSnackbar,
} from 'ui-components';
import { useDeleteMediaKit } from '~/hooks/api/media-kits/delete';
import { useRouteHistory } from '~/store/routeHistory.store';

import { useDeleteMediaKitStore } from './store';
import { getDeleteRedirectPath } from './utils';

export const DeleteMediaKitModal = () => {
  const snackbar = useSnackbar();
  const router = useRouter();
  const deleteMediaKit = useDeleteMediaKit();
  const { history } = useRouteHistory();

  const { isModalOpen, mediaKitId, reset } = useDeleteMediaKitStore();

  // Memoized redirect path calculation
  const redirectPath = useMemo(() => getDeleteRedirectPath(history), [history]);

  const handleDelete: React.MouseEventHandler<HTMLButtonElement> = async (
    evt,
  ) => {
    evt.preventDefault();
    evt.stopPropagation();

    if (!mediaKitId) {
      // Catch edge case
      snackbar.error({
        description: 'No media kit selected. Please try again.',
      });
      reset();
      return;
    }

    deleteMediaKit.mutate({ id: mediaKitId });
    reset();

    router.replace(redirectPath, undefined, { shallow: true });
  };

  return (
    <Modal isOpen={isModalOpen} onClose={reset} onOverlayClick={reset}>
      <ModalHeaderDefault title="Delete Media Kit" />
      <ModalBody>
        <Paragraph size="lg">
          This media kit will be removed. Are you sure you want to delete it?
        </Paragraph>
      </ModalBody>
      <ModalFooterDefault
        secondary={{
          ariaLabel: 'cancel',
          onClick: reset,
          title: 'Cancel',
          trackId: 'delete-media-kit-modal-cancel-btn',
        }}
        primary={{
          ariaLabel: 'delete',
          variant: 'danger',
          onClick: handleDelete,
          title: 'Delete',
          isLoading: deleteMediaKit.isLoading,
          trackId: 'delete-media-kit-modal-delete-btn',
        }}
      />
    </Modal>
  );
};
